'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { print } from 'graphql';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'use-intl';

import { centralAudioElementAtom } from '@/shared-ui';
import {
  type AudioMixer,
  cleanupAudioMixer,
  connectCentralAudioToMixer,
  createAudioMixer,
  supportsDisplayMedia,
} from '@/shared-utilities';
import { UPDATE_INSIGHT_RECORDING } from '@/survey-graphql';

import { getIntentionalUtExit } from './project-usability-browser-session.atoms';
import { wireAudioElementIntoMixerGraph } from './project-usability-recording-manager.helpers';
import {
  stateManager,
  RECORDING_CHUNK_DURATION_MS,
} from './project-usability-recording-state-manager';

// Helper function to send GraphQL mutation via sendBeacon
function sendDurationUpdate(insightId: string, duration: number): boolean {
  const mutation = print(UPDATE_INSIGHT_RECORDING);
  const payload = JSON.stringify({
    query: mutation,
    variables: {
      input: {
        insightId,
        duration: Math.floor(duration),
      },
    },
  });
  const blob = new Blob([payload], { type: 'application/json' });
  return navigator.sendBeacon('/survey/graphql', blob);
}

interface UseRecordingManagerOptions {
  onChunk: (chunk: Blob) => void;
  onRecordingComplete?: (duration: number | null) => void;
  isAiVoiceEnabled?: boolean;
  insightId?: string | null;
  skipLocalMic?: boolean;
}

export function useRecordingManager({
  onChunk,
  onRecordingComplete,
  isAiVoiceEnabled = false,
  insightId = null,
  skipLocalMic = false,
}: UseRecordingManagerOptions) {
  const t = useTranslations('Usability');
  const audioMixerRef = useRef<AudioMixer | null>(null);
  const insightIdRef = useRef<string | null>(insightId);
  const onRecordingCompleteRef = useRef(onRecordingComplete);

  const [error, setError] = useState<string | null>(null);

  const centralAudioElement = useAtomValue(centralAudioElementAtom);

  const start = async () => {
    if (stateManager.getRecorder()) {
      return;
    }
    if (!supportsDisplayMedia()) {
      setError(t('unsupportedBrowserMessage'));
      return;
    }
    setError(null);
    stateManager.setRequestingPermission(true);

    try {
      const screen = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
        // @ts-expect-error experimental flag
        preferCurrentTab: true,
      });

      const [videoTrack] = screen.getVideoTracks();

      videoTrack.addEventListener('mute', () => {
        stateManager.setScreenShareMuted(true);
      });

      videoTrack.addEventListener('unmute', () => {
        stateManager.setScreenShareMuted(false);
      });

      const frameDetectionVideo = document.createElement('video');
      frameDetectionVideo.srcObject = screen;
      frameDetectionVideo.muted = true;
      frameDetectionVideo.playsInline = true;
      frameDetectionVideo.play().catch(() => undefined);

      let lastFrameTime = Date.now();
      let frameCallbackActive = true;

      const onFrame = () => {
        lastFrameTime = Date.now();
        if (stateManager.getSnapshot().isScreenShareMuted) {
          stateManager.setScreenShareMuted(false);
        }
        if (frameCallbackActive) {
          frameDetectionVideo.requestVideoFrameCallback(onFrame);
        }
      };

      let frameCheckInterval: ReturnType<typeof setInterval> | null = null;

      if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
        frameDetectionVideo.requestVideoFrameCallback(onFrame);

        frameCheckInterval = setInterval(() => {
          if (videoTrack.readyState === 'ended') {
            if (frameCheckInterval) clearInterval(frameCheckInterval);
            return;
          }
          const elapsed = Date.now() - lastFrameTime;
          if (
            elapsed > 2000 &&
            !stateManager.getSnapshot().isScreenShareMuted
          ) {
            stateManager.setScreenShareMuted(true);
          }
        }, 1000);
      }

      videoTrack.addEventListener('ended', () => {
        if (frameCheckInterval) clearInterval(frameCheckInterval);
        frameCallbackActive = false;
        frameDetectionVideo.pause();
        frameDetectionVideo.srcObject = null;

        // Treat stopping the share as permission denial
        stateManager.setPermissionDenied(true);
        setError(t('screenShareStoppedError'));

        const recorder = stateManager.getRecorder();
        if (recorder && recorder.state === 'recording') {
          recorder.stop();
        }
      });

      // Get microphone stream (skip when in mobile UT mode inside Hyperbeam)
      let micStream: MediaStream | null = null;
      if (!skipLocalMic) {
        try {
          micStream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: false,
          });
        } catch {
          stateManager.setPermissionDenied(true);
          stateManager.setRequestingPermission(false);
          setError(t('micPermissionRequiredError'));
          return;
        }
      }

      // Create audio mixer with microphone
      const audioMixer = createAudioMixer(micStream);
      audioMixerRef.current = audioMixer;

      // Connect central audio element if available
      if (centralAudioElement && isAiVoiceEnabled) {
        connectCentralAudioToMixer(audioMixer, centralAudioElement);
      }

      // Create combined stream: screen video + master audio destination
      const combined = new MediaStream([
        ...screen.getVideoTracks(),
        ...audioMixer.masterDestination.stream.getAudioTracks(),
      ]);

      const mime =
        [
          'video/webm;codecs=vp9,opus',
          'video/webm;codecs=vp8,opus',
          'video/webm',
        ].find((type) => MediaRecorder.isTypeSupported(type)) || '';

      const rec = new MediaRecorder(
        combined,
        mime ? { mimeType: mime } : undefined
      );

      rec.addEventListener('dataavailable', (e) => {
        if (e.data && e.data.size > 0) {
          onChunk(e.data);
        }
      });
      rec.addEventListener('start', () => {
        stateManager.startClock(); // Start the clock when recording begins
        stateManager.setRecordingState(true);
        // Clear the pending-permission flag only once recording is truly live,
        // so the welcome modal doesn't briefly flash "Continue" between the
        // getUserMedia resolution and MediaRecorder actually starting.
        stateManager.setRequestingPermission(false);
        stateManager.updateActivity(); // Initialize activity tracking
      });
      rec.addEventListener('stop', async () => {
        if (audioMixerRef.current) {
          cleanupAudioMixer(audioMixerRef.current);
          audioMixerRef.current = null;
        }

        stateManager.stopClock();

        // Recording has stopped but the upload is still finalizing — this
        // keeps the saving modal open until the bytes are actually on GCS.
        stateManager.setRecordingState(false);
        stateManager.setFinalizingState(true);

        // Larger 1MB chunks for the remaining flush — fewer requests/failure points.
        stateManager.signalRecordingStopped();

        // Let any final ondataavailable chunks land.
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Only drain/finalize if the upload actually initialized. If the
        // signed-URL request failed there's no uploadRef, so draining would
        // just burn its wall-clock cap with nowhere to send bytes.
        if (stateManager.hasUpload()) {
          // CRITICAL: drain the state-manager's local buffer FIRST. On weak
          // networks trailing chunks may still sit in stateManager.buffer;
          // finalize would not see them and the tail would be silently dropped.
          try {
            await stateManager.drainBuffer();
          } catch {
            // Best effort; fall through to finalize anyway.
          }

          try {
            await stateManager.waitForUploads();
          } catch {
            // Best effort.
          }
        }

        // Finalize and settle into one of three outcomes: clean success
        // (report duration + close), non-terminal stall (keep the modal open
        // and surface Retry — buffer/session preserved), or terminal error
        // (surface it and close WITHOUT claiming the full duration, since the
        // GCS object may be truncated or nothing reached GCS).
        const settle = async () => {
          // No uploadRef means the upload never initialized — treat as a
          // terminal error, never a success, so we don't report a duration
          // for a recording that never reached GCS.
          if (!stateManager.hasUpload()) {
            setError(t('recorderError'));
            stateManager.setStalledState(false);
            stateManager.reset();
            stateManager.setFinalizingState(false);
            return;
          }

          try {
            await stateManager.finalize();
          } catch {
            // Best effort; inspect state below.
          }

          const stuck = stateManager.isUploadStuck();
          const errored = stateManager.uploadHasError();

          if (!stuck && !errored) {
            stateManager.setStalledState(false);
            onRecordingCompleteRef.current?.(stateManager.getExactDuration());
            stateManager.reset();
            stateManager.setFinalizingState(false);
            return;
          }

          if (stuck && !errored) {
            // Weak-network stall (no confirmed-byte progress for a while).
            // Keep the modal open and let the UI show a Retry affordance.
            stateManager.setStalledState(true);
            return;
          }

          setError(t('recorderError'));
          stateManager.setStalledState(false);
          stateManager.reset();
          stateManager.setFinalizingState(false);
        };

        // Wire the saving modal's Retry button to re-drive finalize.
        stateManager.setRetryOrchestrator(async () => {
          stateManager.setStalledState(false);
          await stateManager.retryStuckUpload();
          await settle();
        });

        await settle();
      });

      rec.addEventListener('error', () => {
        setError(t('recorderError'));
        stateManager.setRecordingState(false);
      });

      // Set the recorder in state manager
      stateManager.setRecorder(rec, screen, micStream);

      // Recording started successfully - reset permission denied state
      stateManager.setPermissionDenied(false);

      // Initialize activity tracking for timeout
      stateManager.updateActivity();

      rec.start(RECORDING_CHUNK_DURATION_MS);
    } catch (e) {
      // Cleanup audio mixer on error
      if (audioMixerRef.current) {
        cleanupAudioMixer(audioMixerRef.current);
        audioMixerRef.current = null;
      }

      const errorMessage = e instanceof Error ? e.message : '';
      setError(t('failedToStartRecording'));
      stateManager.setRecordingState(false);
      stateManager.setRequestingPermission(false);
      // Check if this is a permission denied error
      if (
        e instanceof Error &&
        (e.name === 'NotAllowedError' ||
          e.name === 'NotReadableError' ||
          errorMessage.includes('Permission denied') ||
          errorMessage.includes('User denied'))
      ) {
        stateManager.setPermissionDenied(true);
        setError(t('permissionRequiredError'));
      }
    }
  };

  const stop = useCallback(() => {
    if (audioMixerRef.current) {
      cleanupAudioMixer(audioMixerRef.current);
      audioMixerRef.current = null;
    }

    stateManager.stop();
  }, []);

  useEffect(() => {
    onRecordingCompleteRef.current = onRecordingComplete;
    insightIdRef.current = insightId;
  });

  useEffect(
    () =>
      wireAudioElementIntoMixerGraph({
        audioMixerRef,
        centralAudioElement,
        isAiVoiceEnabled,
      }),
    [centralAudioElement, isAiVoiceEnabled]
  );

  // Handle page unload - warn user if recording in progress, then finalize and send duration
  useEffect(() => {
    // Show warning when user tries to leave during recording
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const recorder = stateManager.getRecorder();

      // Send final duration using GraphQL mutation via sendBeacon
      const confirmedDuration = stateManager.getConfirmedDuration();
      if (insightIdRef.current && confirmedDuration > 0) {
        sendDurationUpdate(insightIdRef.current, confirmedDuration);
      }

      if (
        recorder &&
        recorder.state === 'recording' &&
        !getIntentionalUtExit()
      ) {
        // Show browser warning dialog
        // Modern browsers ignore the custom message and show their own warning
        event.preventDefault();
        event.returnValue = ''; // Required for Chrome
      }
    };

    // Cleanup when page is actually unloading (user confirmed the warning)
    // Use 'unload' instead of 'pagehide' - unload only fires when page is actually unloading,
    // not when user cancels or page is being cached
    const handleUnload = () => {
      const recorder = stateManager.getRecorder();
      if (recorder && recorder.state === 'recording') {
        // Use synchronous finalize with keepalive to ensure completion during page unload
        stateManager.finalizeSync();

        // Send final duration using GraphQL mutation via sendBeacon
        const confirmedDuration = stateManager.getConfirmedDuration();
        if (insightIdRef.current && confirmedDuration > 0) {
          sendDurationUpdate(insightIdRef.current, confirmedDuration);
        }
      }
    };

    // Some browsers/power modes suspend the mixer's AudioContext when the tab
    // is hidden, which silently halts the mic → MediaRecorder audio graph.
    // Resume it when the tab returns so recording continues to capture audio.
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      const ctx = audioMixerRef.current?.audioContext;
      if (ctx && ctx.state === 'suspended') {
        ctx.resume().catch(() => {
          // ignore
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stop();
    };
  }, [stop]);

  return { error, start, stop };
}
